<!--
  @component StatusNote — calm capability-state note.
  tier:      primitive (visual atom)
  purpose:   the honest, friendly replacement for FaceLedgerNotice's
             "NOT_WIRED" banner. Tells the user where a capability
             stands in plain language — never a backend term, never a
             dead control posing as real.

  Tones:
    info    — neutral context.
    setup   — needs provisioning; invites a primary action.
    pending — works once the deploy connection is live ("Connect to enable").
    soon    — genuinely future; a subtle note.
    success — confirmation.
-->
<script lang="ts" module>
  export type StatusTone = "info" | "setup" | "pending" | "soon" | "success";

  export interface StatusNoteProps {
    tone?: StatusTone;
    title?: string;
    message?: string;
    /** Optional inline action button (right-aligned). */
    actionLabel?: string;
    onAction?: () => void;
    /** Compact one-line variant — no card chrome. */
    inline?: boolean;
  }
</script>

<script lang="ts">
  let {
    tone = "info",
    title,
    message,
    actionLabel,
    onAction,
    inline = false,
  }: StatusNoteProps = $props();
</script>

<div class="note {tone}" class:inline role="note">
  <span class="dot" aria-hidden="true"></span>
  <div class="body">
    {#if title}<p class="title">{title}</p>{/if}
    {#if message}<p class="msg">{message}</p>{/if}
  </div>
  {#if actionLabel && onAction}
    <button type="button" class="action" onclick={onAction}>{actionLabel}</button>
  {/if}
</div>

<style>
  .note {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-md);
    background: var(--glass-fill);
    font-family: var(--type-font);
  }
  .note.inline {
    padding: 0;
    border: 0;
    background: transparent;
    align-items: center;
  }
  .dot {
    width: 7px;
    height: 7px;
    border-radius: var(--radius-pill);
    margin-top: 5px;
    flex-shrink: 0;
    background: var(--text-tertiary);
  }
  .note.inline .dot { margin-top: 0; }
  .setup   .dot { background: var(--text-secondary); }
  .pending .dot { background: var(--warning); opacity: 0.7; }
  .soon    .dot { background: var(--text-ghost); }
  .success .dot { background: var(--success); }

  .body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
  .title {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
  }
  .msg {
    margin: 0;
    font-size: 12.5px;
    line-height: 1.55;
    color: var(--text-secondary);
  }
  .soon .title, .soon .msg { color: var(--text-tertiary); }

  .action {
    flex-shrink: 0;
    appearance: none;
    background: var(--bg-active);
    color: var(--text-primary);
    border: 0.5px solid var(--border-strong);
    border-radius: var(--radius-md);
    padding: 6px var(--space-3);
    font: inherit;
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
    transition: background var(--ease-fast), border-color var(--ease-fast);
  }
  .action:hover { background: var(--bg-selected); border-color: var(--border-focus); }
</style>
